import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import { MobileAwareWebsiteLink } from 'components/MobileAwareWebsiteLink/MobileAwareWebsiteLink';

import { getListingAmenities } from 'helpers/getListingAmenities';
import { normalizeUrl } from 'helpers/normalizeUrl';

import type { DirectoryCardFields } from 'hooks/useDirectoryConfig/types';
import type { FreeHotelItem } from 'hooks/useDirectoryData/types';

import { htmlToPlainText } from 'views/DirectoryView/helpers/htmlToPlainText';

const ALWAYS_VISIBLE_KEYS: (keyof FreeHotelItem)[] = ['company'];

// Fields must be listed explicitly to render at all; the order here is the order on the card.
// `socials` isn't a listing field: it marks where the row of social logos (`SOCIAL_LINKS`) goes.
const TEXT_FIELD_KEYS: (keyof FreeHotelItem | 'socials')[] = [
  'category',
  'type_name',
  'cat_name',
  'sub_catname',
  'cuisine',
  'rank_name',
  'number_of_rooms',
  'description',
  'description_2',
  'description_3',
  'addr1',
  'addr2',
  'addr3',
  'city',
  'state',
  'zip',
  'country',
  'region',
  'primary_contact_full_name',
  'primary_contact_title',
  'phone',
  'alt_phone',
  'toll_free',
  'fax',
  'email',
  'web_url',
  'socials',
  'notes',
  'official_name',
];

const HTML_FIELD_KEYS: (keyof FreeHotelItem)[] = ['description', 'description_2', 'description_3'];

// Rendered as a row of clickable logos instead of text lines; the order here is the order on the card (social media is generic option)
const SOCIAL_LINKS: { key: keyof FreeHotelItem; icon: IconProp; labelKey: string }[] = [
  { key: 'instagram', icon: ['fab', 'instagram'], labelKey: 'directoryProfileView.socialInstagram' },
  { key: 'facebook', icon: ['fab', 'facebook'], labelKey: 'directoryProfileView.socialFacebook' },
  { key: 'twitter', icon: ['fab', 'x-twitter'], labelKey: 'directoryProfileView.socialTwitter' },
  { key: 'youtube', icon: ['fab', 'youtube'], labelKey: 'directoryProfileView.socialYoutube' },
  { key: 'pinterest', icon: ['fab', 'pinterest'], labelKey: 'directoryProfileView.socialPinterest' },
  { key: 'social_media', icon: ['fas', 'link'], labelKey: 'directoryProfileView.socialMedia' },
];

export interface FreeHotelProps {
  id: string;
  flyToMarker: () => void;
  hotelInfo: () => void;
  hotelName: string;
  hotel: FreeHotelItem;
  cardFields: DirectoryCardFields;
  brandImageUrl?: string | null;
  segmentImageUrl?: string | null;
}

export function FreeHotel({
  id,
  flyToMarker,
  hotelInfo,
  hotelName,
  hotel,
  cardFields,
  brandImageUrl,
  segmentImageUrl,
}: FreeHotelProps) {
  const textFields = [
    ...ALWAYS_VISIBLE_KEYS,
    ...TEXT_FIELD_KEYS.filter((key) => {
      return key === 'socials' || (!ALWAYS_VISIBLE_KEYS.includes(key) && cardFields[key]);
    }),
  ];
  const tags = getListingAmenities(hotel).filter(({ column }) => {
    return cardFields[column];
  });
  const photoUrl = hotel.img_path?.trim();
  const socials = SOCIAL_LINKS.flatMap((social) => {
    const value = hotel[social.key];
    const url = typeof value === 'string' ? value.trim() : '';
    return cardFields[social.key] && url ? [{ ...social, url }] : [];
  });

  return (
    <div className="freeDiv" id={id}>
      <div className="iconDiv">
        <span
          className="fa-layers fa-fw fa-2x markerCircle"
          onClick={flyToMarker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              flyToMarker();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={i18n.translate('directoryView.viewOnMap') as string}
          key="map"
        >
          <FontAwesomeIcon icon={['fas', 'circle']} className="circle" />
          <FontAwesomeIcon icon={['fas', 'map-marker-alt']} className="marker" />
        </span>
      </div>
      <div className="freeInfoDiv" onClick={hotelInfo}>
        {cardFields.img_path && photoUrl && (
          // TODO: Optionally report failing images to sentry.
          <img className="free-hotel-photo" src={photoUrl} alt={`${hotelName} photo`} loading="lazy" />
        )}
        <div className="content">
          {cardFields.brand && brandImageUrl && (
            <img className="freeHotelImg logo-top" src={brandImageUrl} alt={`${hotelName} brand logo`} />
          )}
          {textFields.map((key) => {
            if (key === 'socials') {
              if (socials.length === 0) return null;
              return (
                // The card opens the profile on click. This stops that so a logo only opens its link.
                <div
                  key={key}
                  className="freeHotelSocials"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {socials.map(({ key, icon, labelKey, url }) => {
                    return (
                      <MobileAwareWebsiteLink
                        key={key}
                        url={normalizeUrl(url)}
                        className={clsx('free-hotel-social', `free-hotel-social--${key}`)}
                        ariaLabel={i18n.translate(labelKey) as string}
                      >
                        <FontAwesomeIcon icon={icon} />
                      </MobileAwareWebsiteLink>
                    );
                  })}
                </div>
              );
            }
            const rawHotelParam = hotel[key];
            // Fixes a typing issue by explicitly only allowing plain values to be shown as card text. Excludes objects and arrays (e.g. the `images`).
            // Functionally does not change anything (yet) as only text fields are being included in the list for now anyway.
            if (typeof rawHotelParam !== 'string' && typeof rawHotelParam !== 'number') return null;

            const value = HTML_FIELD_KEYS.includes(key) ? htmlToPlainText(rawHotelParam.toString()) : rawHotelParam;
            if (!value) return null;
            if (key === 'web_url') {
              return (
                // The card opens the profile on click. This span stops that so the link only opens the website.
                <span
                  key={key}
                  data-field={key}
                  className={clsx('free-hotel-detail', 'free-hotel-detail--web_url')}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <MobileAwareWebsiteLink url={normalizeUrl(value.toString())}>
                    {i18n.translate('directoryView.viewWebsite')}
                  </MobileAwareWebsiteLink>
                </span>
              );
            }
            return (
              <span
                key={key}
                data-field={key}
                className={clsx('free-hotel-detail', `free-hotel-detail--${key}`, {
                  freeHotelName: key === 'company', // here for legacy custom scss
                  freeHotelAddr: key === 'city', // here for legacy custom scss
                  'free-hotel-title': key === 'company',
                  'free-hotel-detail--new-section': key === 'city',
                  'free-hotel-detail--small': key === 'sub_catname',
                  'free-hotel-detail--subtitle': key === 'primary_contact_title',
                  'free-hotel-detail--muted': key === 'notes' || key === 'official_name',
                })}
              >
                {value} <br />
              </span>
            );
          })}
          {tags.length > 0 && (
            <div className="freeHotelTags">
              {tags.map(({ column, label }) => {
                return (
                  <span
                    key={column}
                    data-field={column}
                    className={clsx('free-hotel-tag', `free-hotel-tag--${column}`)}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
          {cardFields.segment && segmentImageUrl && (
            <img className="freeHotelImg logo-bottom" src={segmentImageUrl} alt={`${hotelName} segment logo`} />
          )}
          <br />
          <span className="moreInfo">{i18n.translate('directoryView.viewNow')}</span>
          <br />
          <br />
        </div>
      </div>
    </div>
  );
}
